import React, { useEffect, useState } from "react";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import {
  Button,
  ButtonSizes,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import {
  CERTIFICATION_CERTIFICATE_LABEL,
  CERTIFICATION_LABEL,
  CERTIFICATIONS_LABEL,
  ITEMS_ADDED_SUCCESS_LABEL,
  DATA_DELETED_SUCCESS_LABEL,
  DELETE_CERTIFICATION_BODY_LABEL_1,
  DELETE_CERTIFICATION_BODY_LABEL_2,
  DELETE_CERTIFICATION_LABEL,
  DESCRIPTION_LABEL,
  EMPTY_CERTIFICATIONS_MESSAGE,
  EMPTY_CERTIFICATIONS_SELECTED_LABEL,
  EXECUTION_MODE_LABEL,
  GROUP_TARGETS_LABEL,
  INTERNAL_GRADE_IT_LABEL,
  NAME_LABEL,
  URL_LABEL,
  EMPTY_STRING,
  RECIPIENT_LABEL,
} from "../../../../../../constants/Dictionary";
import certificatesImage from "../../../../../../images/certificates.png";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import {
  ReactComponent as PlusIcon,
  ReactComponent as AddIcon,
} from "../../../../../../icons/add.svg";
import { TableFormationCM } from "../TableFormationCM/TableFormationCM";
import { AddCollaboratorCertification } from "./AddCertificationModal/AddCollaboratorCertification";
import { useSearchParams } from "react-router-dom";
import { CollaboratorCmSheetCertification } from "../../../../../../types/api/collaborators.type";
import { EditCollaboratorCertification } from "./EditCertificationModal/EditCollaboratorCertification";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import {
  EmptyStateContainer,
  StyledFormationCM,
} from "../TableFormationCM/TableFormationCM.styled";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../components/core/Link/Link";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import { CertificateModal } from "../../../../../../components/CertificateModal/CertificateModal";
import { CmSheetFooter } from "../../Components/CmSheetFooter/CmSheetFooter";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import { getCollaboratorCertificationsPageColumns } from "../../../../../../constants/TableColumns";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import {
  TextBoxGrid,
  TextBox,
} from "../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { Certification } from "../../../../../../types/api/certifications.type";
import { OperationEnum } from "../../../../../../types/api/generic.type";
import CmSheetCertificateOfCertificationsService from "../../../../../../services/api/cmSheet/CmSheetCertificateOfCertfications.service";
import TargetGroups from "../../../../../../components/TargetGroups/TargetGroups";
import { CmSheetSectionProps } from "../../SchedaCM";
import { findLabelFromValue } from "../../../../../../utils/orgFunctions.utils";
import { roleHasPermission } from "../../../../../../utils/roleAuth.utils";
import {
  DETAIL_QUERY_PARAM_KEY,
  decodeParams,
} from "../../../../../../utils/queryParams.utils";
import {
  CmSheetStateEnum,
  PermissionsEnum,
} from "../../../../../../constants/AppConstants";
import {
  useCmSheetCertifications,
  useUpdateCmSheetCertifications,
} from "../../../../../../hooks/cmSheet/certifications.hook";

export const CollaboratorCertifications = ({
  setHeaderRightContent,
  footer,
  cmSheetInfo,
  isMyProfile,
  cmSheetStateMachineInfo,
  showFooter,
  isInterim,
}: CmSheetSectionProps) => {
  const [searchParams] = useSearchParams();
  const queryParams = decodeParams(searchParams.get(DETAIL_QUERY_PARAM_KEY));
  const collaboratorName = queryParams.collaboratorName ?? null;
  const collaboratorSurname = queryParams.collaboratorSurname ?? null;

  const { orgSettings } = useAppSelector((state) => state.config);
  const { profile } = useAppSelector((state) => state.user);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedCertification, setSelectedCertification] =
    useState<CollaboratorCmSheetCertification | null>(null);
  const [hasData, setHasData] = useState<boolean | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const { data, isFetching } = useCmSheetCertifications(cmSheetInfo.id);

  const { updateCmSheetCertifications, isUpdating: updating } =
    useUpdateCmSheetCertifications(cmSheetInfo.id);

  const onSave = async (saveData: Certification[]) => {
    try {
      const newCertifications = saveData.map((certification) => {
        return {
          operation: OperationEnum.ADD,
          entityId: null,
          data: {
            certificationId: certification.id,
          },
        };
      });
      await updateCmSheetCertifications(
        newCertifications,
        ITEMS_ADDED_SUCCESS_LABEL,
      );
    } finally {
      setHasData(null);
    }
  };

  useEffect(() => {
    setHasData(null);
  }, [cmSheetInfo]);

  useEffect(() => {
    if (!isFetching) {
      if (data && hasData === null) {
        const newhasData = data.length > 0;
        setHasData(newhasData);
      }
    }
  }, [data, hasData, isFetching]);

  useEffect(() => {
    footer(
      <CmSheetFooter
        cmSheetInfo={cmSheetInfo}
        cmSheetStateMachineInfo={cmSheetStateMachineInfo}
        isMyProfile={isMyProfile}
        isInterim={isInterim}
        showEdit={false}
        showFooter={showFooter}
      />,
    );
  }, [hasData, isFetching, updating, cmSheetInfo, cmSheetStateMachineInfo]);

  const canEdit = () => {
    if (!isMyProfile) {
      if (roleHasPermission(profile, PermissionsEnum.EDIT_ALL_CM_SHEETS)) {
        return true;
      }

      if (
        roleHasPermission(profile, PermissionsEnum.EDIT_OWN_CM_SHEET) &&
        profile.id === cmSheetInfo.careerManagerId &&
        [
          CmSheetStateEnum.DRAFT,
          CmSheetStateEnum.INTERMEDIATE_EVALUATION,
        ].includes(cmSheetStateMachineInfo.currentState)
      ) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (canEdit() && hasData) {
      setHeaderRightContent(
        <Link
          onClick={() => setShowAddModal(true)}
          icon={<PlusIcon />}
          size={LinkSizeEnum.SMALL}
          disabled={isFetching || updating}
        >
          {CERTIFICATION_LABEL}
        </Link>,
      );
    } else {
      setHeaderRightContent(null);
    }
  }, [updating, isFetching, hasData]);

  const onCertificate = (certification: CollaboratorCmSheetCertification) => {
    setSelectedCertification(certification);
    certification.fileName && certification.fileName !== ""
      ? setShowCertificateModal(true)
      : setShowEditModal(true);
  };

  const onDelete = (certification: CollaboratorCmSheetCertification) => {
    setShowDeleteModal(true);
    setSelectedCertification(certification);
  };

  const onEdit = (certification: CollaboratorCmSheetCertification) => {
    setShowEditModal(true);
    setSelectedCertification(certification);
  };

  const expandRow = (
    record: CollaboratorCmSheetCertification,
  ): React.ReactElement | null => {
    return (
      <>
        {!record.certification.adHocCollaborator && (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: GROUP_TARGETS_LABEL,
                text: <TargetGroups cc={record.certification.cc} />,
              },
              {
                label: INTERNAL_GRADE_IT_LABEL,
                text:
                  record.certification.internalGrade &&
                  record.certification.internalGrade.length > 0
                    ? record.certification.internalGrade.join(", ")
                    : EMPTY_STRING,
              },
            ]}
          />
        )}
        {record.certification.adHocCollaborator ? (
          <TextBoxGrid
            numCol={3}
            items={[
              {
                label: RECIPIENT_LABEL,
                text: record.certification.collaborator
                  ? `${record.certification.collaborator.surname} ${record.certification.collaborator.name}`
                  : EMPTY_STRING,
              },
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.executionModes,
                  record.certification.executionMode,
                ),
              },
              {
                label: URL_LABEL,
                text: record.certification.url,
              },
            ]}
          />
        ) : (
          <TextBoxGrid
            numCol={2}
            items={[
              {
                label: EXECUTION_MODE_LABEL,
                text: findLabelFromValue(
                  orgSettings.certifications.executionModes,
                  record.certification.executionMode,
                ),
              },
              {
                label: URL_LABEL,
                text: record.certification.url,
              },
            ]}
          />
        )}
        <TextBox
          label={DESCRIPTION_LABEL}
          text={record.certification.description}
        />
      </>
    );
  };

  const rowKey = (record: CollaboratorCmSheetCertification) => {
    return record.certification.id.toString();
  };

  const emptyState = (isLoading: boolean) => {
    return (
      <EmptyStateContainer>
        {isLoading ? (
          <Loader loading={isLoading} />
        ) : (
          <EmptyState
            label={canEdit() ? EMPTY_CERTIFICATIONS_SELECTED_LABEL : ""}
            imageSrc={certificatesImage}
          >
            {canEdit() && (
              <Button
                label={CERTIFICATIONS_LABEL}
                variant={ButtonVariants.SECONDARY}
                size={ButtonSizes.REGULAR}
                icon={<AddIcon />}
                onClick={() => setShowAddModal(true)}
              />
            )}
          </EmptyState>
        )}
      </EmptyStateContainer>
    );
  };

  const handleDeleteCertification = async () => {
    try {
      if (!selectedCertification) {
        return;
      }
      const updatedCertification = [
        {
          operation: OperationEnum.DELETE,
          entityId: selectedCertification.id,
          data: null,
        },
      ];
      await updateCmSheetCertifications(
        updatedCertification,
        DATA_DELETED_SUCCESS_LABEL,
      );
    } finally {
      setHasData(null);
      setShowDeleteModal(false);
      setSelectedCertification(null);
    }
  };

  const certificateApi = (certificationId: number) =>
    CmSheetCertificateOfCertificationsService.get(
      cmSheetInfo.id,
      certificationId,
    );

  const filterData = (data: CollaboratorCmSheetCertification[]) => {
    return searchValue
      ? data.filter((item) =>
          item.certification.name
            .toLowerCase()
            .includes(searchValue.toLowerCase()),
        )
      : data;
  };

  const filteredData = data ? filterData(data) : [];

  return (
    <>
      {showAddModal && (
        <AddCollaboratorCertification
          isOpen={showAddModal}
          onConfirm={onSave}
          cmSheetInfo={cmSheetInfo}
          closeModal={() => setShowAddModal(false)}
        />
      )}
      {selectedCertification && (
        <CertificateModal
          isOpen={showCertificateModal}
          label={CERTIFICATION_CERTIFICATE_LABEL}
          closeModal={() => {
            setShowCertificateModal(false);
            setSelectedCertification(null);
          }}
          {...(selectedCertification && {
            certificateApi: () => certificateApi(selectedCertification.id),
          })}
          idKeyApi={`collaborator-certification-${selectedCertification.id}`}
        />
      )}
      {selectedCertification && (
        <EditCollaboratorCertification
          isOpen={showEditModal}
          cmSheetCertification={selectedCertification}
          closeModal={() => {
            setShowEditModal(false);
            setSelectedCertification(null);
          }}
          cmSheetInfo={cmSheetInfo}
        />
      )}
      {selectedCertification && (
        <PopupConfirm
          isOpen={showDeleteModal}
          label={DELETE_CERTIFICATION_LABEL}
          popupText={`${DELETE_CERTIFICATION_BODY_LABEL_1} ${selectedCertification.certification.name} ${DELETE_CERTIFICATION_BODY_LABEL_2} ${collaboratorSurname} ${collaboratorName}?`}
          isLoading={updating}
          onConfirm={handleDeleteCertification}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedCertification(null);
          }}
        />
      )}
      {!hasData ? (
        emptyState(isFetching || updating)
      ) : (
        <StyledFormationCM>
          <div className="cm-formation-header-container">
            <DoubleStructure
              verticalAlignment={AlignmentEnum.CENTER}
              variant="primary"
            >
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {`${filteredData.length} ${filteredData.length === 1 ? CERTIFICATION_LABEL : CERTIFICATIONS_LABEL}`}
              </BodyTyp>
              <div className="filters">
                <SearchInput
                  allowClear
                  value={searchValue}
                  onChange={setSearchValue}
                  label={NAME_LABEL}
                  name={"search"}
                />
              </div>
            </DoubleStructure>
          </div>
          <TableFormationCM<CollaboratorCmSheetCertification>
            data={filteredData}
            scrollY={"calc(100dvh - 490px)"}
            loading={isFetching}
            headers={getCollaboratorCertificationsPageColumns(
              onCertificate,
              isMyProfile ?? false,
              orgSettings,
            )}
            {...(canEdit() && {
              onEdit: onEdit,
              onDelete: onDelete,
            })}
            rowKey={rowKey}
            expandable={{
              expandedRowRender: expandRow,
            }}
            emptyText={EMPTY_CERTIFICATIONS_MESSAGE}
          />
        </StyledFormationCM>
      )}
    </>
  );
};
